// © Microsoft Corporation. All rights reserved.

/**
 * Provides utility functions to get and set data from IndexedDB.
 * If dbName is not provied, this class creates a db with name MicrosoftNews.
 * If storeName is not provied, this class creates a table/store with name Default.
 * Recommended to create a new table for your feature to separate and iterate through entries quickly.
 * IndexedDB utility does not require stringify and json parsing so more efficient! Just store your object as value.
 */
export class IndexedDb {

    /** IndexedDb object */
    private idb: IDBFactory = indexedDB;

    /** IndexedDb store name */
    /** DO NOT PUT DOT IN THE STORE NAME. */
    private storeName: string;

    /** IndexedDb promise. Sets when IndexedDB is initialized. */
    private idbPromise: Promise<IDBDatabase>;

    /**
     * Returns if IndexedDb is supported.
     * @returns true if supported else false.
     */
    public get supported(): boolean {
        if (!this.idb) {
            return false;
        }

        return true;
    }

    /**
     * Function looks at query string param and check if IndexedDb operation is allowed or not. By default it is allowed.
     * @returns true if supported else false.
     */
    public get isGetAllAllowed(): boolean {
        if (IDBObjectStore.prototype && IDBObjectStore.prototype.getAll) {
            return true;
        }

        return false;
    }

    /**
     * This should be used for unit testing only.
     * @public
     * @param {IDBFactory} idb - IndexedDB object to override.
     */
    public _setIndexedDb(idb: IDBFactory): void {
        this.idb = idb;
    }

    /**
     * This should be used for unit testing only.
     * @public
     * @return {Promise<IDBDatabase>} existing promise.
     */
    public _getIdbPromise(): Promise<IDBDatabase> {
        return this.idbPromise;
    }

    /**
     * Returns object from IndexedDB
     * @public
     * @param {IDBValidKey} key - IndexedDB key. Typically it can be string or any supported type.
     * @returns Promise<Type>
     */
    public async getObject<Type>(key: IDBValidKey): Promise<Type> {
        let req: IDBRequest;

        await this.withIdbStore("readonly", "getObject", storeCallback => {
            req = storeCallback.get(key);
        });
        return req.result;
    }

    /**
     * Return all objects from IndexedDB using getAll implementation.
     * @public
     * @returns Promise<Type>
     */
    public async getAll<Type>(): Promise<Type[]> {
        let req: IDBRequest;
        await this.withIdbStore("readonly", "getAll", storeCallback => {
            req = storeCallback.getAll();
        });
        return req.result;
    }

    /**
     * Returns all objects from IndexedDB.
     * if getAll is supported, call it else fallback to Cursor implementation that exists on wide range of devices.
     * @public
     * @returns Promise<any>
     */
    public async getAllObjects<Type>(): Promise<any> {
        if (this.isGetAllAllowed) {
            return this.getAll();
        }

        const returnArray: Type[] = [];
        await this.withIdbStore("readonly", "getAllObjects", storeCallback => {
            storeCallback.openCursor().onsuccess = function (event: any): void {
                const cursor = event.target && event.target.result ? event.target.result : undefined;
                if (cursor && cursor.value) {
                    returnArray.push(cursor.value);
                    cursor.continue();
                }
            };
        });
        return returnArray;
    }

    /**
     * Sets object into IndexedDB
     * @public
     * @param {IDBValidKey} key - IndexedDB key. Typically it can be string or any supported type.
     * @param {any} value - Any typed value you want to insert into IndexedDB.
     */
    public async setObject(key: IDBValidKey, value: any): Promise<void> {
        return await this.withIdbStore("readwrite", "setObject", storeCallback => {
            storeCallback.put(value, key);
        });
    }

    /**
     * Removes object from IndexedDB
     * @public
     * @param {IDBValidKey} key - IndexedDB key. Typically it can be string or any supported type.
     */
    public async removeObject(key: IDBValidKey): Promise<void> {
        return await this.withIdbStore("readwrite", "removeObject", storeCallback => {
            storeCallback.delete(key);
        });
    }

    /**
     * Removes all objects from IndexedDB
     * @public
     */
    public async clearObjects(): Promise<void> {
        return await this.withIdbStore("readwrite", "clearObjects", storeCallback => {
            storeCallback.clear();
        });
    }

    /**
     * Initialize IndexedDb.
     * @param idbStoreName {string} - IndexedDB store name
     * @param dbName {string} - Database name
     * @param preInitializedIdbPromise {Promise<IDBDatabase>} - Optional pre-initialized promise that is fetching the {IDBDatabase}  object.
     *  Used to optimize/prime the IDB connection cost so it can be started as early as possible in the page without incurring additional cost
     *  later when lot more things are happening on the page.
     */
    public async initializeIndexedDb(
        idbVersion = 1,
        idbStoreName = "Default",
        dbName = "MicrosoftNews",
        preInitializedIdbPromise: Promise<IDBDatabase> = null
    ): Promise<void> {

        this.storeName = idbStoreName;

        this.idbPromise = preInitializedIdbPromise || new Promise((resolve, reject) => {

            const openreq = this.idb.open(dbName, idbVersion);

            openreq.onerror = () => {
                reject(openreq.error);
            };

            openreq.onsuccess = () => {
                resolve(openreq.result);
            };

            openreq.onupgradeneeded = () => {
                openreq.result.createObjectStore(idbStoreName);
            };
        });
    }

    /**
     * Private function to set transaction.
     * @param type - IDB read/write mode
     * @param operationName - readable name to use in the app trace logging
     * @param callback - the callback to invoke with the IDB objectStore.
     * @returns {Promise<void>} async operation that ends when the IDB transaction is complete.
     */
    private async withIdbStore(
        type: IDBTransactionMode,
        operationName: string,
        callback: ((store: IDBObjectStore) => void)
    ): Promise<void> {

        return this.idbPromise.then(db => new Promise<void>((resolve, reject) => {
            const transaction = db.transaction(this.storeName, type);

            transaction.oncomplete = () => {
                resolve();
            };

            transaction.onabort = transaction.onerror = () =>
                reject(transaction.error);

            callback(transaction.objectStore(this.storeName));
        }));
    }
}